import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import { Avatar, Box } from "@mui/material";
import GCBackButton from "../../../components/GreenCity/GCBackButton";
import ChatSpace from "../../../components/GreenCity/ChatSpace";
import AddChatUserList from "../../../components/Models/AddChatUserList";
import LoadingModal from "../../../components/LoadingModal";
import MessageModal from "../../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../../components/ErrorMessageModal";
import { Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import appServices from "../../../services/app-services/appServices";
import { Message } from "../../../util/types";
import {
  convertUserImage,
  isClosedGroup,
  isGroupChat,
  isMultiPersonChat
} from "../../../util/utils";
import GroupIconImage from "../../../components/GreenCity/GroupIconImage";

const UserChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = location.state;
  const { user } = useSelector((state: any) => state.userAuth);
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openAddUserModal, setOpenAddUserModal] = useState<boolean>(false);
  const [chatInfo, setChatInfo] = useState<any>();
  const [groupData, setGroupData] = useState<any>();
  const [currentChatUsers, setCurrentChatUsers] = useState<any[]>([]);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [lastMessageId, setLastMessageId] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [showErrorMessageModal, setShowErrorMessageModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [errorRenderKey, setErrorRenderKey] = useState<string>("");

  useEffect(() => {
    apiCall();
  }, []);

  useEffect(() => {
    if (!openAddUserModal) {
      setShowMessageModal(false);
    }
  }, [openAddUserModal]);

  const apiCall = async () => {
    try {
      setIsLoading(true)
      const res = await appServices.getChatMessages(chatId)
      const chatData = res.data.chat

      setChatInfo(chatData)
      setCurrentChatUsers(chatData.users.map((chatUser: any) => {
        return {
          id: chatUser.id,
          name: chatUser.name,
          nickname: chatUser.nickname,
          image: chatUser.image
        }}
      ))

      setAllMessages(chatData.messages);
      if (chatData.messages.length > 0) {
        setLastMessageId(chatData.messages[chatData.messages.length - 1].id);
      }

      if (chatData.member_group) setGroupData(chatData.member_group)
    } catch (error: any) {
      console.error(error)
      setErrorMessage(error?.response?.data?.message ?? T("api_unknown_error"));
      setErrorRenderKey(error?.response?.data?.return_to ?? "")
      setShowErrorMessageModal(true)
    } finally {
      setIsLoading(false)
    }
  }

  const readonly = useMemo(() => {
    return chatInfo && groupData && isGroupChat(chatInfo.chat_type) && isClosedGroup(groupData?.closed_at)
  }, [groupData, chatInfo])

  const handleSelect = async (selectedUser: any) => {
    try {
      setIsLoading(true)
      await appServices.addUserToChat(chatInfo.id, selectedUser.id, user.auth_token);

      const updatedChatUsers = [
        ...currentChatUsers,
        {
          id: selectedUser?.id,
          name: selectedUser?.name,
          nickname: selectedUser?.nickname,
          image: selectedUser?.image,
        },
      ];
      setCurrentChatUsers(updatedChatUsers);
      setShowMessageModal(true)

      setTimeout(() => {
        setOpenAddUserModal(false)
      }, 3000)

      return true
    } catch (error: any) {
      console.error("Failed to add user to chat:", error);
      setErrorMessage(error?.response?.data?.message);
      setErrorRenderKey(error?.response?.data?.return_to ?? "");
      setShowErrorMessageModal(true);
      return false
    } finally {
      setIsLoading(false)
    }
  };

  const getNewMessages = () => {
    if (!chatInfo?.id) return
    appServices.getLatestMessages(chatInfo.id, lastMessageId || 0, user.auth_token)
      .then((res) => {
        if (res.data.success === false) {
          navigate(-1);
          return;
        }

        if (res.data.messages.length > 0) {
          const newMessages = res.data.messages.sort((a: any, b: any) => a.id - b.id);
          setAllMessages([...allMessages, ...newMessages]);
          setLastMessageId(newMessages.at(-1).id);
        }
        setIsLoading(false);
        setPendingMessage(null);
      })
      .catch((err) => {
        console.error(err)
        setErrorMessage(err?.response?.data?.message ?? T("api_unknown_error"));
        setErrorRenderKey(err?.response?.data?.return_to ?? "")
        setShowErrorMessageModal(true)
        setIsLoading(false);
      });
  };

  const closeErrorModal = () => {
    switch (errorRenderKey) {
      case "my_page":
        navigate("/myfarm")
        break
      case "no_return":
        break
      default:
        navigate(-1);
    }
    setShowErrorMessageModal(false)
  }

  const handleNavigateChatUsers = () => {
    if (!chatInfo) return
    navigate("/chat_users", {state: { chatId: chatInfo.id }})
  }

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => getNewMessages(), 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [lastMessageId, chatInfo]);

  const handleSendMessage = async (message: string) => {
    try {
      setPendingMessage(message);
      setIsLoading(true);
      await appServices.sendMessage(message, user.auth_token, chatInfo.id)
      getNewMessages()
    } catch (error: any) {
      console.error(error)
      setErrorMessage(error?.response?.data?.message ?? T("api_unknown_error"))
      setErrorRenderKey(error?.response?.data?.return_to ?? "")
      setShowErrorMessageModal(true)
      setIsLoading(false)
    }
  };

  const isOneToManyChat =
    isMultiPersonChat(chatInfo?.chat_type, currentChatUsers)

  const displayUserImage = () => {
    if (isGroupChat(chatInfo?.chat_type)) return

    const image = isOneToManyChat ?
      displayUserInfo(true)?.image :
      displayUserInfo(chatInfo?.creator_id !== user.id)?.image

    return convertUserImage(image)
  }

  const displayChatTitle = () => {
    let title;
    if (isGroupChat(chatInfo?.chat_type)) {
      title = chatInfo?.member_group?.name
    } else {
      const dispUser = isOneToManyChat ?
        displayUserInfo(true) :
        displayUserInfo(chatInfo?.creator_id !== user.id)

      title = dispUser?.nickname || dispUser?.name
    }
    return title
  }

  const displayUserInfo = (expectCreator: boolean) => {
    const chatCreator = currentChatUsers.find((chatUser: any) => chatUser.id === chatInfo?.creator_id)
    if (expectCreator) return chatCreator

    const targetUser = currentChatUsers.find((chatUser: any) => chatUser.id !== chatInfo?.creator_id)
    // 相手がいなければ作成者を返す
    return targetUser ?? chatCreator
  }

  return (
    <Box sx={style.container}>
      <LoadingModal visible={isLoading}/>

      <Box sx={style.headerBox}>
        <div style={style.goBack}>
          <GCBackButton onClick={() => navigate(-1)} />
        </div>
        <Box sx={style.groupContainer}>
          <Box sx={style.memberContainer}>
            <Box sx={style.eventPostingBox}>
              {isGroupChat(chatInfo?.chat_type) ? (
                <GroupIconImage
                  groupId={chatInfo?.member_group?.id}
                  alt={chatInfo?.member_group?.name}
                  style={style.avatarIcon}
                />
              ) : (
                <Avatar src={displayUserImage()} sx={style.avatarIcon} />
              )}
              <Typography sx={isOneToManyChat ? style.chatTitleWithCount : style.chatTitle}>
                {displayChatTitle()}
              </Typography>
              {isOneToManyChat && (
                <Typography style={style.userCount}>
                  +{currentChatUsers.length}
                </Typography>
              )}
            </Box>

            {currentChatUsers.length > 1 &&
              <Typography sx={style.chatUsers} onClick={() => handleNavigateChatUsers()}>{T("user_chat_mambers")}</Typography>
            }
          </Box>
          {!readonly && <AddCircleOutlineIcon sx={style.addIcon} onClick={() => setOpenAddUserModal(true)} />}
        </Box>
      </Box>

      <ChatSpace
        currentUser={user}
        messages={allMessages}
        handleSendMessage={handleSendMessage}
        pendingMessage={pendingMessage}
        readonly={readonly}
      />

      <AddChatUserList
        chatInfo={chatInfo}
        open={openAddUserModal}
        onClose={() => setOpenAddUserModal(false)}
        onSelect={handleSelect}
        groupData={groupData}
        currentUserId={user.id}
        currentChatUsers={currentChatUsers}
      />

      <MessageModal
        open={showMessageModal}
        message={T("user_chat_added")}
        onClose={() => {}}
      />

      <ErrorMessageModal
        open={showErrorMessageModal}
        messages={errorMessage}
        onClose={() => closeErrorModal()}
      />
    </Box>
  );
};

export default UserChat;

const style = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
  },
  goBack: {
    marginLeft: "-10px",
  },
  headerBox: {
    padding: "100px 16px 10px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
  },
  groupContainer: {
    display: "flex",
    alignItems: "center",
  },
  memberContainer: {
    display: "flex",
    gap: "10px",
    width: "calc(100% - 32px)",
    marginTop: "5px",
  },
  eventPostingBox: {
    display: "flex",
    alignItems: "center",
    paddingTop: "3px",
  },
  memberTitle: {
    fontWeight: "700",
  },
  chatTitle: {
    fontWeight: "700",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "35vw",
  },
  chatTitleWithCount: {
    fontWeight: "700",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "28vw",
  },
  userCount: {
    fontWeight: "700",
    marginLeft: "0",
    lineHeight: "30px",
  },
  chatUsers: {
    fontSize: "0.875rem",
    letterSpacing: 0,
    whiteSpace: "nowrap",
    paddingTop: "3px",
    lineHeight: "30px",
    fontWeight: 400,
  },
  addIcon: {
    color: "#4792E0",
    fontSize: "35px",
    cursor: "pointer",
    paddingTop: "3px",
    marginTop: "5px",
    justifyContent: "flex-end",
  },
  avatarIcon: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    marginRight: "0.3rem"
  },
};
